import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { minutesToSeconds } from "date-fns";

const nvdbApiUrl = "https://nvdbapiles.atlas.vegvesen.no";
/** The Egenskapstype linking a model object to its Vegobjekt, by name. Its id differs per Vegobjekttype. */
const lookupEgenskapstype = "ProsjektInternObjekt_ID";

export type NvdbVegobjekttype = {
    navn: string;
    /** The id of the type's `ProsjektInternObjekt_ID` Egenskapstype, if it has one. */
    lookupEgenskapstypeId: number | undefined;
    /** Egenskapstype ids in the datakatalog's sorting order, which Vegkart lists Egenskaper in. */
    egenskapstypeOrder: number[];
};

/** An Egenskap that reads as `navn: verdi`. */
export type NvdbEgenskap = {
    id: number;
    navn: string;
    verdi: string | number | boolean;
    enhet?: { kortnavn: string };
};

export type NvdbVegobjekt = { id: number; egenskaper: NvdbEgenskap[] };

/** The most Vegobjekter a search shows. */
export const maxVegobjekter = 10;

type RawEgenskap = Omit<NvdbEgenskap, "verdi"> & { egenskapstype: string; verdi: unknown };

/** Egenskapstyper that aren't readable as `navn: verdi`. */
const hiddenEgenskapstyper = ["Geometri", "Liste"];

function isReadable(egenskap: RawEgenskap): egenskap is RawEgenskap & NvdbEgenskap {
    return (
        !hiddenEgenskapstyper.includes(egenskap.egenskapstype) &&
        ["string", "number", "boolean"].includes(typeof egenskap.verdi)
    );
}

export const nvdbApi = createApi({
    reducerPath: "nvdbApi",
    baseQuery: fetchBaseQuery({
        baseUrl: nvdbApiUrl,
        // Required by NVDB's tracking rules. Nothing identifying the project or org is sent.
        prepareHeaders: (headers) => {
            headers.set("X-Client", "novorender");
            return headers;
        },
    }),
    keepUnusedDataFor: minutesToSeconds(10),
    endpoints: (builder) => ({
        /** `null` when the datakatalog has no Vegobjekttype with the id. */
        getVegobjekttype: builder.query<NvdbVegobjekttype | null, number>({
            query: (typeId) => ({
                url: `/datakatalog/api/v1/vegobjekttyper/${typeId}?inkluder=egenskapstyper`,
                // An unknown type is an answer, not a failure, so it's cached rather than retried.
                validateStatus: (response) => response.ok || response.status === 404,
                responseHandler: "content-type",
            }),
            transformResponse: (
                res: {
                    navn: string;
                    egenskapstyper?: { id: number; navn: string; sorteringsnummer?: number }[];
                },
                meta,
            ) => {
                if (meta?.response?.status === 404) {
                    return null;
                }
                const egenskapstyper = res.egenskapstyper ?? [];
                return {
                    navn: res.navn,
                    lookupEgenskapstypeId: egenskapstyper.find((e) => e.navn === lookupEgenskapstype)?.id,
                    egenskapstypeOrder: egenskapstyper
                        .toSorted((a, b) => (a.sorteringsnummer ?? Infinity) - (b.sorteringsnummer ?? Infinity))
                        .map((e) => e.id),
                };
            },
        }),
        /** `more` when there are Vegobjekter beyond the first `maxVegobjekter`. */
        searchVegobjekter: builder.query<
            { vegobjekter: NvdbVegobjekt[]; more: boolean },
            { typeId: number; egenskapstypeId: number; objektkode: string }
        >({
            query: ({ typeId, egenskapstypeId, objektkode }) => {
                // NVDB decodes the filter value once more after the query string, so the Objektkode is
                // encoded twice: once here, once by URLSearchParams. Otherwise `+` turns into a space.
                const params = new URLSearchParams({
                    inkluder: "egenskaper",
                    // One more than shown, to tell whether there are more.
                    antall: String(maxVegobjekter + 1),
                    egenskap: `egenskap(${egenskapstypeId})="${encodeURIComponent(objektkode)}"`,
                });
                return `/vegobjekter/api/v4/vegobjekter/${typeId}?${params}`;
            },
            transformResponse: (res: { objekter: { id: number; egenskaper?: RawEgenskap[] }[] }) => ({
                vegobjekter: res.objekter.slice(0, maxVegobjekter).map(({ id, egenskaper = [] }) => ({
                    id,
                    egenskaper: egenskaper.filter(isReadable),
                })),
                more: res.objekter.length > maxVegobjekter,
            }),
        }),
    }),
});

export const { useGetVegobjekttypeQuery, useSearchVegobjekterQuery } = nvdbApi;
